import type { ReactNode } from "react";
import { type GetProps, isWeb, styled, YStack } from "tamagui";
import { DecorativeTrailing } from "../DecorativeTrailing";
import { CalendarText } from "../Formatting";
import { disabledInteractiveStyles, webInteractiveTransition, webPointerProps } from "../interactive/styles";
import { type OptionalInteractiveProps, useInteractiveElement } from "../interactive/useInteractiveElement";
import { RowLayout } from "../Row/RowLayout";
import { Body1, Body2 } from "../Typography";

// `color: "inherit"` (not `$textDefault`) — ListItem is a content container,
// so color flows from inner text components rather than being forced here.
const listItemInteractiveVariantStyles = {
  color: "inherit",
  cursor: "pointer",
  hoverStyle: {
    backgroundColor: "$surface1Hover"
  },
  pressStyle: {
    backgroundColor: "$surface1Hover",
    scale: 0.98
  }
} as const;

// YStack so the single RowLayout child fills width via the default
// `alignItems: stretch`. RowLayout owns its own internal alignment + gap;
// the outer container's only job is padding, background, and interactive
// styling.
const StyledListItem = styled(YStack, {
  name: "ListItem",
  paddingVertical: "$sp1.5",
  paddingHorizontal: "$sp2",
  variants: {
    selected: {
      true: {
        backgroundColor: "$surface0"
      },
      false: {
        backgroundColor: "$transparent"
      }
    },
    interactive: {
      true: listItemInteractiveVariantStyles,
      false: {}
    },
    disabled: {
      true: disabledInteractiveStyles,
      false: {}
    }
  } as const,
  defaultVariants: {
    selected: false,
    interactive: false
  } as const
});
type StyledListItemProps = GetProps<typeof StyledListItem>;
const MetadataText = styled(Body2, {
  color: "$textSubtle",
  flexShrink: 0,
  numberOfLines: 1
});
type MetadataProps = {
  metadata?: string;
  metadataDate?: never;
} | {
  metadata?: never;
  metadataDate?: string | Date | null;
};
type DataTrailingProps = {
  data: string;
  trailing?: never;
} | {
  data?: never;
  trailing?: DecorativeTrailing;
};
type ListItemVisualProps = {
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  title: string;
  titleNumberOfLines?: number;
  subtitle?: string;
  subtitleNumberOfLines?: number;
  "aria-label"?: string;
  children?: never;
};
export type ListItemProps = Omit<StyledListItemProps, "onPress" | "href" | "disabled" | "interactive"> & MetadataProps & DataTrailingProps & OptionalInteractiveProps & ListItemVisualProps;

// a11y/focus + transition props aren't valid inside variant values — applied at JSX time.
const listItemInteractiveBehaviorStyles = {
  ...webPointerProps,
  ...webInteractiveTransition
} as const;
export const ListItem = ({
  leftElement,
  rightElement,
  title,
  titleNumberOfLines = 1,
  subtitle,
  subtitleNumberOfLines = 1,
  metadata,
  metadataDate,
  data,
  trailing,
  href,
  onPress,
  replace,
  scroll,
  shallow,
  disabled,
  ...rest
}: ListItemProps) => {
  const {
    isInteractive,
    renderTag,
    interactiveProps
  } = useInteractiveElement({
    onPress,
    href,
    replace,
    scroll,
    shallow,
    disabled
  });
  const interactiveBehaviorStyles = !isInteractive || disabled ? {} : listItemInteractiveBehaviorStyles;
  const trailingNode = trailing ? <DecorativeTrailing {...trailing} /> : data ? <Body1 color="$textDefault" flexShrink={0} numberOfLines={1}>
      {data}
    </Body1> : null;
  const secondaryTrailingNode = metadataDate ? <CalendarText date={metadataDate} element={MetadataText} /> : metadata ? <MetadataText>{metadata}</MetadataText> : null;
  return <StyledListItem interactive={isInteractive} disabled={disabled} {...interactiveBehaviorStyles} {...renderTag ? {
    render: renderTag
  } : {}} {...interactiveProps} style={isWeb && renderTag === "a" ? {
    textDecoration: "none"
  } : undefined} {...rest} data-sentry-element="StyledListItem" data-sentry-component="ListItem" data-sentry-source-file="ListItem.tsx">
      <RowLayout leading={leftElement} primary={<Body1 color="$textDefault" flexShrink={1} numberOfLines={titleNumberOfLines}>
            {title}
          </Body1>} primaryTrailing={trailingNode} secondary={subtitle ? <Body2 color="$textSubtle" flexShrink={1} numberOfLines={subtitleNumberOfLines}>
              {subtitle}
            </Body2> : undefined} secondaryTrailing={secondaryTrailingNode} rowTrailing={rightElement} data-sentry-element="RowLayout" data-sentry-source-file="ListItem.tsx" />
    </StyledListItem>;
};